import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { seoIntegrationStates, seoRunIngestion } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Pill, Loading, Empty, DataTable, stateTone, stateLabel } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/integrations")({
  head: () => ({ meta: [{ title: "Integrations · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: SeoIntegrations,
});

function SeoIntegrations() {
  const load = useServerFn(seoIntegrationStates);
  const run = useServerFn(seoRunIngestion);
  const q = useQuery({ queryKey: ["seo-integrations"], queryFn: () => load() });
  const ingest = useMutation({
    mutationFn: () => run({ data: { days: 5 } }),
    onSuccess: (res: any) => {
      toast.success(res?.ingest?.message ?? "Ingestion finished");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Ingestion failed"),
  });

  const d = q.data as any;

  return (
    <SeoPage
      title="Integrations"
      description="Every data source the SEO system uses, with an honest status. Optional paid sources are never required."
      actions={
        <Button size="sm" onClick={() => ingest.mutate()} disabled={ingest.isPending}>
          {ingest.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Fetch latest search data
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <Panel title="Core sources" subtitle={`Canonical site: ${d?.host ?? ""}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Row
                label="Google Search Console"
                state={d?.gsc?.state}
                detail={d?.gsc?.detail}
                extra={[
                  d?.gsc?.siteUrl ? `Property: ${d.gsc.siteUrl}` : null,
                  d?.gsc?.lastSuccessAt ? `Last data: ${new Date(d.gsc.lastSuccessAt).toLocaleString()}` : null,
                ]}
              />
              <Row
                label="Google Analytics 4"
                state={d?.ga4?.state}
                detail={
                  d?.ga4?.measurementId
                    ? "Measurement tag is live with consent mode."
                    : "Add a GA4 measurement ID to start collecting visit data."
                }
                extra={[d?.ga4?.connected ? "Reporting account connected." : null]}
              />
              <Row
                label="Instant indexing (IndexNow / Bing)"
                state={d?.indexnow?.state}
                detail={
                  d?.indexnow?.state === "ok"
                    ? "New and updated pages are submitted automatically."
                    : "Needs an IndexNow key before anything can be submitted."
                }
              />
              <Row label="Ranking provider" state={d?.rank?.state} detail={d?.rank?.detail} />
            </div>
          </Panel>

          <Panel title="Optional sources" subtitle="The system works without these; they simply add extra detail.">
            <div className="grid gap-3 sm:grid-cols-2">
              {(d?.optional ?? []).map((p: any) => (
                <Row key={p.id} label={p.label} state={p.state} detail={p.purpose} />
              ))}
            </div>
          </Panel>

          <Panel title="Recent indexing submissions">
            {d?.indexnow?.attempts?.length ? (
              <DataTable
                head={["When", "Page", "Service", "Result"]}
                rows={d.indexnow.attempts.map((a: any) => [
                  new Date(a.created_at).toLocaleString(),
                  a.url,
                  a.provider,
                  <Pill tone={a.status === "submitted" ? "good" : a.status === "failed" ? "bad" : "warn"}>
                    {a.status.replace(/_/g, " ")}
                  </Pill>,
                ])}
              />
            ) : (
              <Empty>Nothing submitted yet.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}

function Row({
  label,
  state,
  detail,
  extra,
}: {
  label: string;
  state?: string;
  detail?: string;
  extra?: Array<string | null>;
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold">{label}</p>
        <Pill tone={stateTone(state ?? "unconfigured")}>{stateLabel(state ?? "unconfigured")}</Pill>
      </div>
      {detail ? <p className="mt-1 text-sm text-muted-foreground">{detail}</p> : null}
      {(extra ?? []).filter(Boolean).map((x) => (
        <p key={x as string} className="mt-1 text-xs text-muted-foreground">
          {x}
        </p>
      ))}
    </div>
  );
}
